/**
 * PrimeIndexChart — stylised SVG line chart showing the Dubai Prime
 * Property Index growth 2022 → 2026. Matches brand: cream background,
 * Cormorant Garamond for big numerics, Montserrat labels, gold line
 * + dots with a soft gold gradient fill underneath.
 */
const POINTS = [
  { year: "2022", value: 100 },
  { year: "2023", value: 108 },
  { year: "2024", value: 122 },
  { year: "2025", value: 137 },
  { year: "2026", value: 142 },
];

export default function PrimeIndexChart() {
  // Chart geometry
  const W = 500;
  const H = 320;
  const padL = 50;
  const padR = 24;
  const padT = 30;
  const padB = 56;

  const minV = 90;
  const maxV = 160;
  const xs = POINTS.map((_, i) => padL + (i * (W - padL - padR)) / (POINTS.length - 1));
  const ys = POINTS.map((p) => padT + ((maxV - p.value) / (maxV - minV)) * (H - padT - padB));

  const linePath = xs.map((x, i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${ys[i].toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L ${xs[xs.length - 1].toFixed(1)} ${(H - padB).toFixed(1)} L ${xs[0].toFixed(1)} ${(H - padB).toFixed(1)} Z`;

  const yTicks = [160, 140, 120, 100];

  return (
    <div data-testid="prime-index-chart" className="w-full">
      <span className="underline-label mb-6 inline-block">Prime Index · 2022 — 2026</span>

      <div className="aspect-[5/3.4] w-full">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="primeFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#c5a059" stopOpacity="0.32" />
              <stop offset="100%" stopColor="#c5a059" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Y grid + labels */}
          {yTicks.map((tick) => {
            const y = padT + ((maxV - tick) / (maxV - minV)) * (H - padT - padB);
            return (
              <g key={tick}>
                <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="#1c1c1c" strokeOpacity="0.07" />
                <text
                  x={padL - 10}
                  y={y + 4}
                  textAnchor="end"
                  fontFamily="Cormorant Garamond, serif"
                  fontSize="14"
                  fill="#3d2b1f"
                >
                  {tick}
                </text>
              </g>
            );
          })}

          {/* Soft gold area */}
          <path d={areaPath} fill="url(#primeFill)" />

          {/* Gold line */}
          <path d={linePath} fill="none" stroke="#c5a059" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

          {/* Data dots + values */}
          {POINTS.map((p, i) => (
            <g key={p.year}>
              <circle cx={xs[i]} cy={ys[i]} r="5" fill="#c5a059" stroke="#f5f0e8" strokeWidth="2" />
              <text
                x={xs[i]}
                y={ys[i] - 14}
                textAnchor="middle"
                fontFamily="Cormorant Garamond, serif"
                fontSize="15"
                fill="#1c1c1c"
              >
                {p.value}
              </text>
            </g>
          ))}

          {/* X axis labels */}
          {POINTS.map((p, i) => (
            <text
              key={p.year}
              x={xs[i]}
              y={H - padB + 26}
              textAnchor="middle"
              fontFamily="Montserrat, sans-serif"
              fontSize="10"
              letterSpacing="2"
              fill="#3d2b1f"
              fillOpacity="0.7"
            >
              {p.year.toUpperCase()}
            </text>
          ))}
        </svg>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-6 max-w-sm">
        <div>
          <div className="font-display text-3xl text-[#1c1c1c]">+42pts</div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#3d2b1f]/70 mt-1">5-Yr Index Gain</div>
        </div>
        <div>
          <div className="font-display text-3xl text-[#1c1c1c]">+21.3%</div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#3d2b1f]/70 mt-1">2025 → 2026</div>
        </div>
      </div>
    </div>
  );
}
